import React, {useEffect, useState} from 'react';
import {
    ActionButton,
    ContentWrapper,
    DialogEditActionsWrapper,
    Locales,
    LocalesContentWrapper,
    LocalesSelectionWrapper,
    LocalesWrapper
} from './AdvancedLocalePicker.style';
import {t} from 'i18next';
import {config} from '../../init/config';
import {countryResource} from './resources/CountryResource';
import {languageResource} from './resources/LanguageResource';
import {countryLanguageResource} from './resources/CountryLanguageResource';
import {CountryPicker} from './CountryPicker';
import {LanguagePicker} from './LanguagePicker';
import {ParentLocales} from './ParentLocales';
import EventEmitter from 'events';
import {AdvancedLocalePicker_addAllLocales} from '../../init/events';
import {getFontSize, isNullOrEmpty} from '../../utils';
import {generateIdentifier} from '../../utils/IdentityUtils';
import {DropdownOptionProps} from '../../models/DropdownProps';
import {DROPDOWN_OPTION, getDropdownOptionProps, SELECT_LANGUAGE} from '../../utils/DropdownOptionPropsUtils';

export interface LocalesSelectionProps {
    readonly defaultLocale: string;
    readonly locales: string[];
    readonly parentLocales: string[];
    readonly setParentLocales: boolean;
    readonly eventEmitter: EventEmitter;
    onChange: (changedLocales: string[]) => void;
    onReset: () => void;
}

const getLocaleLabel = (locale: string) => {
    let localeLabel = '';
    let labelFormat = locale.split('_');
    if (labelFormat.length > 2) {
        return localeLabel;
    }

    let language = labelFormat[0];
    let country = labelFormat[1];
    if ((!isNullOrEmpty(language)) && (!isNullOrEmpty(country))) {
        localeLabel = `/${country.toLowerCase()}/${language.toLowerCase()}`;
    }

    return localeLabel;
};

const getCountryDataSource = (): DropdownOptionProps[] => {
    return getDropdownOptionProps(countryResource
        .filter(country => ((config.DLS_COUNTRIES.length === 0)
            || ((config.DLS_COUNTRIES.length > 0) && (config.DLS_COUNTRIES.includes(country.value.toUpperCase()))))), SELECT_LANGUAGE, DROPDOWN_OPTION);
};

const createLocale = (languageSelection: string, countrySelection: string): string => {
    return `${languageSelection.toLowerCase()}_${countrySelection.toUpperCase()}`;
};

const filterLocales = (locales: string[]): string[] => {
    let filteredLocales: string[] = [];
    let localeItem: string[] = [];
    if ((locales) && (locales.length > 0)) {
        filteredLocales = locales
            .map(locale => locale)
            .filter(locale => {
                localeItem = locale.split('_');
                if (localeItem.length >= 2) {
                    return (((config.DLS_COUNTRIES.length === 0)
                            || ((config.DLS_COUNTRIES.length > 0) && (config.DLS_COUNTRIES.includes(localeItem[1].toUpperCase()))))
                        && ((config.DLS_LANGUAGES.length === 0)
                            || ((config.DLS_LANGUAGES.length > 0) && (config.DLS_LANGUAGES.includes(localeItem[0].toLowerCase())))))
                }
                return false;
            });
    }

    return filteredLocales;
};

/**
 * @name AdvancedLocalePicker
 * @description
 * Component to make a selection of string tags that can describe an item
 */
export const AdvancedLocalePicker: React.VFC<LocalesSelectionProps> = (props) => {
    const getCountryLanguageMap = (): Map<string, string[]> => {
        let countryLanguageScope = new Map<string, string[]>();
        let parentLocales = ((props.parentLocales) && (props.parentLocales.length > 0)) ? props.parentLocales : [];
        let localeData: string[] = [];
        let countryKey = '';
        let language = '';
        let languages: string[] = [];
        parentLocales
            .map(locale =>
                locale.split(',')
                    .map(localeItem => {
                        localeData = localeItem.split('_');
                        language = localeData[0].toLowerCase();
                        countryKey = localeData[1].toUpperCase();
                        languages = [];
                        let tempLanguages = countryLanguageScope.get(countryKey);
                        if (tempLanguages) {
                            languages = tempLanguages;
                        }
                        if ((!languages.includes(locale))
                            && ((config.DLS_LANGUAGES.length === 0)
                                || ((config.DLS_LANGUAGES.length > 0) && (config.DLS_LANGUAGES.includes(language))))) {
                            languages = languages.concat(language);
                        }
                        countryLanguageScope.set(countryKey, languages);
                    })
            );

        if (countryLanguageScope.size === 0) {
            countryLanguageResource.map(countryLanguage =>
                countryLanguageScope
                    .set(countryLanguage.value,
                        countryLanguageResource
                            .filter(countryLanguageItem => (countryLanguage.value === countryLanguageItem.value))
                            .filter(countryLanguageItem => ((config.DLS_LANGUAGES.length === 0)
                                || ((config.DLS_LANGUAGES.length > 0) && (config.DLS_LANGUAGES.includes(countryLanguageItem.name.toLowerCase())))))
                            .map(countryLanguageItem => countryLanguageItem.name.toLowerCase())));
        }

        return countryLanguageScope;
    };

    const getCountryOptions = (countrySelectionState: string[]): DropdownOptionProps[] => {
        return getCountryDataSource()
            .filter(country => (countryLanguageScope.has(country.value)))
            .filter(country => ((countrySelectionState.length === 0)
                || ((countrySelectionState.length > 0) && (countrySelectionState.includes(country.value)))));
    };

    const getCountryLanguageScope = (countryLanguageScope: Map<string, string[]>, countrySelectionState: string): string[] => {
        let countryLanguages: string[] = [];
        let tempCountryLanguages = countryLanguageScope.get(countrySelectionState);
        if (tempCountryLanguages) {
            countryLanguages = tempCountryLanguages;
        }

        return countryLanguages;
    };

    const getLanguageOptions = (countrySelectionState: string): DropdownOptionProps[] => {
        let countryLanguages: string[] = getCountryLanguageScope(countryLanguageScope, countrySelectionState);
        return languageResource.filter(language => (countryLanguages.includes(language.value)));
    };

    const getParentLocaleDatasource = (): string[] => {
        let parentLocalesResult: string[] = [];
        if ((props.parentLocales) && (props.parentLocales.length > 0)) {
            parentLocalesResult = filterLocales(props.parentLocales);
        }
        if (parentLocalesResult.length === 0) {
            let locales: string[] = [];
            countryLanguageScope.forEach((value, key) => {
                value.map(language => {
                    if ((language.trim().length > 0) && (key.trim().length > 0)) {
                        locales.push(createLocale(language, key))
                    }
                });
                return locales;
            });
            parentLocalesResult = filterLocales(locales);
        }

        return parentLocalesResult;
    };

    const [localeOptions, setLocaleOptions] = useState<string[]>(props.locales ? props.locales : [props.defaultLocale]);
    const [localesInitialState] = useState<string[]>(filterLocales(localeOptions));
    const [countryInput, setCountryInput] = useState('');
    const [languageInput, setLanguageInput] = useState('');
    const [countryReset, setCountryReset] = useState<boolean>(false);
    const [languageReset, setLanguageReset] = useState<boolean>(false);
    const [disableCountry, setDisableCountry] = useState<boolean>(true);
    const [disableLanguage, setDisableLanguage] = useState<boolean>(true);
    const [disableAddLocale, setDisableAddLocale] = useState<boolean>(true);
    const [countryLanguageScope] = useState<Map<string, string[]>>(getCountryLanguageMap());
    const [countryOptions, setCountryOptions] = useState<DropdownOptionProps[]>(getCountryOptions([]));
    const [languageOptions, setLanguageOptions] = useState<DropdownOptionProps[]>([]);
    const [protectedLocales] = useState(localeOptions.filter(locale => (!localesInitialState.includes(locale))));
    const [parentLocales] = useState(getParentLocaleDatasource());

    const getLocales = (): string[] => {
        let locales: string[] = [];
        if ((localeOptions) && (localeOptions.length > 0)) {
            locales = filterLocales(localeOptions);
        }

        return locales;
    };

    const getLanguageScope = (selectedCountry: string, selectedCountries: string[], selectedLocales: string[]): DropdownOptionProps[] => {
        let languageOptionsScope: DropdownOptionProps[] = getLanguageOptions(selectedCountry);
        let isLocalesContainCountry = (selectedCountries.includes(selectedCountry)) as boolean;
        if (isLocalesContainCountry) {
            let remainingLocales: string[] = parentLocales.filter(locale => ((!selectedLocales.includes(locale)) && (selectedCountry === (locale.split('_')[1]).toUpperCase())));
            let remainingLanguages: string[] = remainingLocales.map(locale => ((locale.split('_')[0]).toLowerCase()));
            languageOptionsScope = languageOptionsScope.filter(locale => (remainingLanguages.includes(locale.value)));
        }

        return languageOptionsScope;
    }

    const getDisableCountryLanguageSelection = (selectedLocales: string[]) => {
        let isToDisable = ((parentLocales.length === selectedLocales.length)
            && (parentLocales.filter(locale => (!selectedLocales.includes(locale))).length === 0)) as boolean;
        setCountryReset(isToDisable);
        setLanguageReset(isToDisable);
        setDisableCountry(isToDisable);
        setDisableLanguage(isToDisable);
    }

    const getDisableAddAllLocales = (): boolean => {
        let selectedLocales: string[] = getLocales();
        let locales = parentLocales.filter(locale => (!selectedLocales.includes(locale)));
        return (locales.length === 0);
    }

    const getDisableAddLocale = (countrySelection: string, languageSelection: string): boolean => {
        if ((isNullOrEmpty(countrySelection)) || (isNullOrEmpty(languageSelection))) {
            return true;
        }

        let countryLanguages = getCountryLanguageScope(countryLanguageScope, countrySelection);
        if ((!countryLanguages.includes(languageSelection))) {
            return true;
        }

        let locale = createLocale(languageSelection, countrySelection);
        return getLocales().includes(locale);
    };

    useEffect(() => {
        let selectedLocales: string[] = props.locales ? props.locales : [props.defaultLocale];
        setLocaleOptions(selectedLocales);
        let selectedCountries: string[] = Array.from(new Set(parentLocales
            .filter(locale => (!selectedLocales.includes(locale)))
            .map(locale => ((locale.split('_')[1]).toUpperCase()))));

        let selectedCountry = countryInput;
        if (!selectedCountries.includes(countryInput)) {
            selectedCountry = '';
            setCountryInput('');
        }

        setCountryOptions(getCountryOptions(selectedCountries));
        setLanguageOptions(getLanguageScope(selectedCountry, selectedCountries, selectedLocales));
        getDisableCountryLanguageSelection(selectedLocales);
        setDisableCountry(selectedCountries.length === 0);
        setDisableLanguage(isNullOrEmpty(selectedCountry));
    }, [props.locales]);

    const handleCountryInput = (countrySelection: string[]) => {
        let selectedCountry = countrySelection[0];
        setCountryInput(selectedCountry);
        let selectedLocales: string[] = getLocales();
        let selectedCountries: string[] = Array.from(new Set(selectedLocales.map(locale => ((locale.split('_')[1]).toUpperCase()))));
        setLanguageOptions(getLanguageScope(selectedCountry, selectedCountries, selectedLocales));
        setDisableAddLocale(getDisableAddLocale(selectedCountry, languageInput));
        getDisableCountryLanguageSelection(selectedLocales);
        setCountryReset(false);
        setLanguageReset(true);
    };

    const handleLanguageInput = (languageSelection: string[]) => {
        let selectedLanguage = languageSelection[0];
        setLanguageInput(selectedLanguage);
        setDisableAddLocale(getDisableAddLocale(countryInput, selectedLanguage));
        setCountryReset(false);
        setLanguageReset(false);
    };

    const getItemLocales = (selectedLocales: string[]): string[] => {
        if ((selectedLocales && (selectedLocales.length > 0))) {
            let compensatedLocales = protectedLocales.filter(locale => (!selectedLocales.includes(locale)));
            return selectedLocales.concat(compensatedLocales);
        }

        if ((selectedLocales && (selectedLocales.length === 0))) {
            selectedLocales = selectedLocales.concat(props.defaultLocale);
        }

        return protectedLocales.concat(selectedLocales);
    };

    const saveLocale = () => {
        let locale = createLocale(languageInput, countryInput);
        let selectedLocales = getLocales();
        if (!selectedLocales.includes(locale)) {
            selectedLocales = selectedLocales.concat(locale);
        }

        if (selectedLocales.length === 0) {
            selectedLocales = selectedLocales.concat(props.defaultLocale);
        }

        selectedLocales = getItemLocales(selectedLocales);
        props.onChange(selectedLocales);
        setDisableAddLocale(true);
        getDisableCountryLanguageSelection(selectedLocales);
        setCountryReset(false);
        setLanguageReset(true);
    };

    const saveAllLocales = () => {
        let selectedLocales: string[] = getItemLocales(parentLocales);
        props.onChange(selectedLocales);
        setCountryInput('');
        setDisableAddLocale(true);
        getDisableCountryLanguageSelection(selectedLocales);
        setCountryReset(true);
        setLanguageReset(true);
    };

    const resetSelectedLocales = () => {
        let selectedLocales: string[] = getItemLocales(parentLocales);
        props.onReset();
        setCountryInput('');
        setDisableAddLocale(true);
        getDisableCountryLanguageSelection(selectedLocales);
        setCountryReset(true);
        setLanguageReset(true);
    };

    const handleDeleteLocale = (localeKey: string) => {
        let selectedLocales: string[] = getItemLocales(getLocales().filter(locale => (locale !== localeKey)));
        props.onChange(selectedLocales);
        getDisableCountryLanguageSelection(selectedLocales);
        setDisableLanguage(isNullOrEmpty(countryInput));
    };

    const getParentLocaleLabels = (parentLocales: string[]) => {
        return parentLocales.map(locale => getLocaleLabel(locale));
    };

    props.eventEmitter.on(AdvancedLocalePicker_addAllLocales, saveAllLocales);
    let fontSize = getFontSize([
        t('TAG_SELECTION_COMPONENT_ADD_LABEL'),
        t('TAG_SELECTION_COMPONENT_ADD_ALL_LABEL'),
        t('TAG_SELECTION_COMPONENT_RESET_SELECTION_LABEL')
    ], 4);

    return (
        <LocalesWrapper id={generateIdentifier()}>
            <ContentWrapper id={generateIdentifier()}>
                <ParentLocales key={'parentLocales'}
                               parentLocales={getParentLocaleLabels(parentLocales)}/>
            </ContentWrapper>
            <LocalesSelectionWrapper id={generateIdentifier()}>
                <CountryPicker dataSource={countryOptions}
                               defaultValue={''}
                               resetState={countryReset}
                               resetInput={false}
                               disabled={disableCountry}
                               onChange={handleCountryInput}/>
                <LanguagePicker dataSource={languageOptions}
                                defaultValue={''}
                                resetState={languageReset}
                                resetInput={false}
                                disabled={disableLanguage}
                                onChange={handleLanguageInput}/>
            </LocalesSelectionWrapper>
            <DialogEditActionsWrapper id={generateIdentifier()}>
                <ActionButton id={generateIdentifier()}
                              key='add'
                              icon='add'
                              mode='primary'
                              disabled={disableAddLocale}
                              type='button'
                              label={t('TAG_SELECTION_COMPONENT_ADD_LABEL')}
                              title={t('TAG_SELECTION_COMPONENT_ADD_LABEL')}
                              fontSize={fontSize}
                              onClick={saveLocale}/>
                <ActionButton id={generateIdentifier()}
                              key='addAll'
                              icon='add'
                              mode='secondary'
                              disabled={getDisableAddAllLocales()}
                              type='button'
                              label={t('TAG_SELECTION_COMPONENT_ADD_ALL_LABEL')}
                              title={t('TAG_SELECTION_COMPONENT_ADD_ALL_LABEL')}
                              fontSize={fontSize}
                              onClick={saveAllLocales}/>
                <ActionButton id={generateIdentifier()}
                              key='resetSelection'
                              icon='reset'
                              mode='secondary'
                              disabled={false}
                              type='button'
                              label={t('TAG_SELECTION_COMPONENT_RESET_SELECTION_LABEL')}
                              title={t('TAG_SELECTION_COMPONENT_RESET_SELECTION_LABEL')}
                              fontSize={fontSize}
                              onClick={resetSelectedLocales}/>
            </DialogEditActionsWrapper>
            <LocalesContentWrapper id={generateIdentifier()}>
                {getLocales().map(locale => {
                        return <Locales id={generateIdentifier()}
                                        key={locale}
                                        label={getLocaleLabel(locale)}
                                        buttonClose
                                        onClose={() => (localeOptions.length === 1) ? true : handleDeleteLocale(locale)}/>
                    }
                )}
            </LocalesContentWrapper>
        </LocalesWrapper>
    );
};
